// AI_CONTEXT: {
//   "docs": [
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-001-confirm-email-prompt-step-one",
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-002-change-email-otp-step",
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-003-social-login-prompt",
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-004-signup-otp-flow",
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-010-resend-after-expiry",
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-017-consolidate-resolution-paths",
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-020-emailused-restarts-at-email-entry",
//     "epics/202609212003_CEF_confirm-email-frontend/tickets/CEF-036-mfa-retry-no-captcha",
//     "epics/202610051709_SEV_settings-email-verify-on-change/tickets/SEV-001-primary-email-verify-new-address",
//     "epics/202610051709_SEV_settings-email-verify-on-change/tickets/SEV-002-backup-email-verify-new-address"
//   ],
//   "purpose": "User model: manages cached user state, authentication, login/logout lifecycle and account updates including email verification",
//   "version": "1.3.0",
//   "priority": "core",
//   "stability": "stable"
// }
inherit("User", {
	parent: `Model`,
	singleton: true,
	requires: [`DOM`, `Events`, `Locale`, `Orders`, `Time`, `XHR`],
	constructor(setup) {
		const { App, XHR, data, promises } = this;

		data.user = undefined; // the cached instance of the current user state
		data.cachedUser = undefined; // used for judging whether a real change to the user object has occurred between publishes
		data.requestPending = false; // keep track of whether an ajax request is active
		data.identified = false; // used to avoid triggering the "user-log-in" analytics events more than once
		data.guestUser = null; // used for "guest checkout", where a temp user is generated per-cart
		data.profile = undefined;

		promises.user = null; // the cached promise from the current active request ()
		promises.login = null;

		data.failedLogins = []; // keep an array of emails from failed login attempts

		// NOTE: don't thread into Promise chain, to avoid circular dependencies
		this.require({ deps: [`Social`] });

		XHR.addErrorHandler({
			priority: `high`, // attempt error recovery immediately, before the XHR request's owner receives anythings
			order: 20,
			handler: (reason) => {
				// generic error handling for XHR errors not caught by the XHR handler or consuming module
				const currentUser = data.user;
				let deferred;
				let promise;

				switch (reason.status) {
					case 403: // forbidden error, likely a login expiration
						if (this.isString(App.getCookie(`agentHandle`), 1)) {
							throw reason;
						}

						deferred = this.Deferred();
						promise = deferred.promise;

						if (this.isPromise(promises.login)) {
							// a login recovery attempt is already being made, rely on that outcome
							this.when({
								promise: promises.login,
								then: deferred.resolve,
								fail: deferred.reject,
							});
						} else {
							this.set({
								value: null, // nullify internal value immediately
								skipCallbacks: true, // don't notify subscribers yet
								then: () => {
									const email = this.isObject(currentUser) ? currentUser.email : null;

									// Possible future ticket to add check - if not coming from email, notify user
									// App.notifyUser({
									// 	type: `message`,
									// 	message: `Your session has expired. Please log in again.`,
									// });

									App.loadOverlay({
										type: `LogInOverlay`,
										allowSignUp: false,
										replace: false, // don't replace any existing overlays, in case that's how the 403 originated
										defer: false, // show immediately
										setup: {
											email,
											callbacks: {
												// success - should now be logged in, proceed
												success: (result) => deferred.resolve(),
												// closed - reject if success hasn't already fired
												die: () => deferred.reject(reason),
											},
										},
									});
								},
							});

							promise = this.when({
								promise,
								fail: (reason) => {
									// notify others user is not logged in
									this.set({ value: null });
									throw reason;
								},
							});

							promises.login = this.when({
								promise,
								// don't pass anything that could affect the retry logic
								then: () => {},
								// login cycle is over, reset for next time
								fin: () => delete promises.login,
							});
						}

						return promise; // returning, no break
				}

				throw reason; // Re-throw the error for consumers to handle
			},
		});
	},

	api: {
		/**
		 * Checks if the current user is a guest user.
		 * @method User.isGuestUser
		 * @returns {Boolean} True if the current user is a guest user, false otherwise.
		 */
		isGuestUser: {
			public: true,
			value() {
				return !this.isNull(this.data.guestUser);
			},
		},
		/**
		 * @method User.get
		 * @returns {Promise<User>} A promise that resolves with the current user object, rejects if not logged in.
		 * Fetch the details for the logged-in fan.
		 */
		get: {
			async: false, // needs raw access to async options below
			public: true,
			defaults: {
				force: false, // don't allow returning a cached version
				block: true,
				derive: null,
				require: false, // require a login or reject the promise on 403
				subscribe: false, // call the handler functions on subsequent updates
				reject: false,
				validate: true,
			},
			value(context, options) {
				this.defaults(options, {
					owner: context,
				});

				const { App, XHR, data, promises } = this;
				const { guestUser } = data;
				const { user } = data;
				const { block } = options;

				var forceRequest = options.force || options.require,
					notLoggedIn = this.isUndefined(user) && !this.isString(App.getCookie(`ULIN`)),
					previouslyRejected = this.isNull(user),
					rejected = false,
					promise;

				if (!this.isNull(guestUser)) {
					const { derive } = options;
					options.promise = this.isFunction(derive) ? derive(guestUser) : guestUser;
					return this.when(options);
				}

				if (options.reject || (!forceRequest && (notLoggedIn || previouslyRejected))) {
					promise = this.reject(new Error(`User not logged in.`));
					rejected = true;
				}

				// trigger a new request if there isn't one pending, and there's no cached value
				if (forceRequest || !(data.requestPending || rejected || this.isPromise(promises.user))) {
					promise = XHR.ajax({
						url: `/api/user/profile`,
						ignoreErrors: !options.require, // don't try to force a login unless it's required
						block,
						then(response) {
							data.requestPending = false;

							// sort the payment methods by default
							if (this.isArray(response.paymentMethods, 1)) {
								response.paymentMethods = response.paymentMethods.sort((a, b) => b.default - a.default);
							}

							return this.set({
								value: response,
								validate: options.validate,
							});
						},
						fail(reason) {
							data.requestPending = false;
							this.set({
								value: null,
							});
							throw reason;
						},
					});

					data.requestPending = true;
					promises.user = promise; // cache for later use
				} else if (!this.isPromise(promise)) {
					promise = promises.user;
				}

				if (options.subscribe) {
					// keep track of the handler functions if this is a subscription
					const { owner, then, fail, derive } = options;
					const pending = data.requestPending && !rejected; // if user is in the process of being updated, ignore the handler this time
					this.subscribe({ owner, then, fail, pending, derive });
				}

				if (this.isFunction(options.derive)) {
					promise = this.when({
						promise,
						then: (user) => (user != null ? options.derive(user) : user),
					});
				}

				options.promise = promise;
				return this.when(options);
			},
		},

		subscribe: {
			async: false, // need access to async options below
			public: true,
			defaults: {
				pending: false, // determines whether to ignore the next update
			},
			value(context, options) {
				this.defaults(options, {
					owner: context,
					derive: null,
				});
				this.subscriptions.push({
					handler: options.then,
					errorHandler: options.fail,
					owner: options.owner,
					pending: options.pending,
					derive: options.derive,
					deriveResult: null,
				});
			},
		},

		set: {
			async: true,
			public: true,
			defaults: {
				skipCallbacks: false,
				validate: true,
			},
			value(context, options) {
				const { App, Deferred, Locale, Orders, data, promises } = this;

				this.defaults(options, {
					owner: context,
				});

				let { value } = options;
				const deferred = Deferred();

				if (this.isNull(value)) {
					// user is being 'unset'
					deferred.resolve(null);
				} else {
					if (!this.isArray(value.followedEvents)) {
						value.followedEvents = [];
					}

					if (!this.isArray(value.amplifiedEvents)) {
						value.amplifiedEvents = [];
					}

					if (!data.identified) {
						const method = data.loginMethod;
						App.captureInsight({ eventType: `user-log-in`, method });
						data.identified = true;
					}

					// TODO: Determine how to replace user.orders and user.transfers, currently the usage in MyTickets
					// won't work with pagination and available properties when using the items endpoint
					if (this.isArray(value.orders, 1)) {
						this.each(value.orders, (order) => {
							order.origin = `PURCHASE`;
							Orders.normalize(order);
						});
					} else {
						value.orders = [];
					}

					if (this.isArray(value.transfers)) {
						this.each(value.transfers, (transfer) => {
							transfer.origin = `TRANSFER`;
							Orders.normalize(transfer);
						});
					} else {
						value.transfers = [];
					}

					deferred.resolve(value);
				}

				promises.user = this.when({
					promise: deferred.promise,
					then: (user) => {
						if (this.isObject(user)) {
							this.normalize(user);

							const { defaultLocale, emailHash: userEmailHash, trackingId: userId } = user;

							if (this.isString(defaultLocale)) {
								Locale.setUserDefault({ key: defaultLocale });
							}

							App.setFirstPartyStorage({ userEmailHash, userId });
						}

						data.user = user;

						if (!options.skipCallbacks) {
							this.each(this.subscriptions, (subscription) => {
								if (subscription.pending) {
									// first time being called
									// the promise resolving is responsible for the first call, ignore
									subscription.pending = false;
								} else {
									if (this.isNull(value)) {
										// user was unset / logged out
										if (this.isFunction(subscription.errorHandler)) {
											subscription.errorHandler.call(subscription.owner);
										}
									} else {
										// user was set / updated
										if (this.isFunction(subscription.handler)) {
											const { derive } = subscription;
											const protectedValue = this.clone(value);

											if (this.isFunction(derive)) {
												const deriveResult = derive(protectedValue);

												if (this.isEqual(deriveResult, subscription.deriveResult)) {
													return; // break loop
												}

												subscription.deriveResult = this.clone(deriveResult);
												subscription.handler.call(subscription.owner, deriveResult);
											} else {
												subscription.handler.call(subscription.owner, protectedValue);
											}
										}
									}
								}
							});
						}

						return user;
					},
				});

				return promises.user;
			},
		},

		/**
		 * Calculate locally derived values.
		 */
		normalize: {
			value(user) {
				const { firstName, lastName, id } = user;
				let initials = null;
				let name = null;

				if (this.isString(firstName, 1)) {
					const firstInitial = firstName.charAt(0).toUpperCase();
					const firstRemainder = firstName.slice(1);

					if (this.isString(lastName, 1)) {
						const lastInitial = lastName.charAt(0).toUpperCase();
						initials = `${firstInitial}${lastInitial}`;
					} else {
						initials = firstInitial;
					}

					name = `${firstInitial}${firstRemainder}`;
				} else if (this.isString(lastName, 1)) {
					initials = lastName.charAt(0).toUpperCase();
				}

				const avatar = { initials, name }; // create the avatar
				const trackingId = `u${id}`; // create the tracking ID
				this.assign(user, { avatar, trackingId });
			},
		},

		signUp: {
			async: true,
			public: true,
			defaults: {
				method: `email`,
				followEventId: null,
			},
			async value(context, options) {
				const { App, Locale, XHR, data } = this;
				const { body, followEventId, method = `email` } = options;
				const localeInfo = Locale.get();
				const { selected: selectedLocale } = localeInfo;
				const { key: locale } = selectedLocale;
				const { email, password } = body;
				const eventType = `user-sign-up`; // used for Insights tracking

				switch (method) {
					case `checkout-guest`: {
						// create new guest user
						const { eventId, cartToken } = options;
						const apiPrefix = options.apiPrefix || `/api/events/${eventId}/shoppingcarts`;
						const url = `${apiPrefix}/${cartToken}/guest-user`;
						const guestUserResponse = await XHR.ajax({ body, REST: true, type: `POST`, url });
						// remap the response to the expected user object schema
						const {
							account,
							creditsApplicability,
							creditsAvailableAmount,
							login,
							id,
							paymentIntent,
							paymentMethods,
							purchasedHoldQuantityMap,
							purchasedSaleQuantityMap,
						} = guestUserResponse;
						const { email, emailHash, temp } = login;
						const { billingAddresses, countryCodeAlpha2, emailOptin, firstName, groupOptIn, lastName } = account;
						const guestUser = {
							billingAddresses,
							countryCodeAlpha2,
							creditsApplicability,
							creditsAvailableAmount,
							email,
							emailHash,
							emailOptin,
							firstName,
							groupOptIn,
							id,
							lastName,
							paymentIntent,
							paymentMethods,
							purchasedHoldQuantityMap,
							purchasedSaleQuantityMap,
							temp,
						};

						this.normalize(guestUser);
						data.guestUser = guestUser;
						data.identified = true; // prevent user-log-in from being tracked

						this.triggerCallbacks({ override: guestUser });

						App.captureInsight({ eventType, method: `guest` });

						return guestUser;
					} // returning, no break

					case `email`: {
						let url = `/api/users?action=signup`;
						if (this.isInt(followEventId, 1)) {
							url += `&follow=${followEventId}`;
						}

						const type = `POST`;
						const REST = true;

						body.locale = locale;
						await XHR.ajax({ body, REST, type, url });

						// clear any failed login attempts for this email
						this.extract(data.failedLogins, { email });

						data.identified = true; // prevert the user-log-in analytics tracking from firing

						const user = await this.logIn({
							method: `email`,
							email,
							password,
						});

						App.captureInsight({ eventType, method: `email` });

						return user;
					} // returning, no break
				}
			},
		},

		register: {
			async: true,
			public: true,
			async value(context, options) {
				const { XHR } = this;
				const { data: body } = options;
				const user = await this.get();
				const { id: userId } = user;
				const url = `/api/users/${userId}?action=register&version=2`;
				const response = await XHR.ajax({ body, REST: true, type: `PUT`, url });

				// this call returns the updated user object
				this.set({ skipCallbacks: true, value: response });

				return response;
			},
		},

		confirmFacebook: {
			async: true,
			public: true,
			defaults: {
				id: null,
			},
			async value(context, options) {
				const { XHR } = this;
				const { data: body, id: userId } = options;

				if (!this.isInt(userId)) {
					throw new Error(`User.confirmFacebook(): options.id is required`);
				}

				const url = `/api/users/${userId}?action=fbconfirm&version=2`;
				const response = await XHR.ajax({ body, REST: true, type: `PUT`, url });

				// this call returns the updated user object
				this.set({ skipCallbacks: true, value: response });

				return response;
			},
		},

		requestLogin: {
			async: true,
			public: true,
			async value(context, options) {
				try {
					const { App } = this;
					const { followEventId, mode, replaceOverlay = false } = options;
					let { theme } = options;

					if (theme === undefined && this.isObject(context) && this.isObject(context.data)) {
						const { data: contextData } = context;
						const { theme: contextTheme } = contextData;

						if (this.isString(contextTheme)) {
							theme = contextTheme;
						}
					}

					const result = await new Promise((resolve, reject) => {
						const type = `LogInOverlay`;
						const defer = false; // take over any existing overlays
						const callbacks = {
							onLoginFlowExited: reject, // reject the promise if the overlay is closed before success
							success: (user) => resolve(user), // resolve the promise is the overlay reports success
						};
						const setup = { allowSignUp: mode !== `recovery`, callbacks, followEventId, theme };

						App.loadOverlay({ defer, setup, type, replace: replaceOverlay });
					});

					return result;
				} catch (reason) {
					if (reason === undefined) {
						throw new Error(`User.requestLogin: Couldn't obtain user.`);
					} else {
						throw reason;
					}
				}
			},
		},

		logIn: {
			async: true,
			public: true,
			defaults: {
				method: `facebook`,
				skipCallbacks: false,
			},
			async value(context, options) {
				const { App, Locale, Time, XHR, data } = this;
				const { method, skipCallbacks } = options;
				const time = Time.getTime();
				const localeInfo = Locale.get();
				const { selected: selectedLocale } = localeInfo;
				const { key: locale } = selectedLocale;

				data.loginMethod = method;

				switch (method) {
					case `email`: {
						const { email, password } = options;
						const body = { email, password };
						const { failedLogins } = data;

						let attempt = this.find(failedLogins, { email });

						if (this.isObject(attempt)) {
							if (attempt.expiration > time) {
								await this.require({ deps: [`Captcha`] });
								const { Captcha } = this;
								const responseToken = await Captcha.getResponseToken();
								body.recaptchaResponse = responseToken;
							} else {
								this.extract(failedLogins, { email });
								attempt = null;
							}
						}

						try {
							const url = `/login?version=2`;
							const type = `POST`;
							const REST = false;
							const response = await XHR.ajax({ body, REST, type, url });
							var cached = this.clone(response); // deep-clone the response, in case the original is altered by the requester

							this.defer({
								then: () => this.set({ value: cached }),
							});

							// clear any failed login attempts for this email
							this.extract(failedLogins, { email });

							return response;
						} catch (reason) {
							// CEF-036: an MFA challenge means the password was right, so it is not a failed
							// attempt. Recording it made the fan's next try (e.g. after closing the code
							// modal) solve a reCAPTCHA the server doesn't require. Rethrow so LogIn opens the
							// MFA overlay, as develop's SML-010 does.
							if (this.getMfaPreAuthToken(reason)) {
								throw reason;
							}

							const expiration = time + 900000; // 1000ms * 60s * 15m

							if (this.isObject(attempt)) {
								attempt.expiration = expiration;
							} else {
								failedLogins.push({
									email: options.email,
									expiration,
								});
							}

							// CEF-036: throw, not return. A returned error only rejects when it is a jQuery XHR;
							// anything else (e.g. Base.js's JSON.parse failing on a non-JSON 400/401) would
							// resolve logIn and read as a successful login.
							throw reason;
						}

						// returning, no break
					}

					case `facebook`: {
						// get a FB auth token
						let { accessToken } = options;

						if (!this.isString(accessToken, 1)) {
							if (!this.isObject(this.Social)) {
								// avoid blocking unnecessarily
								await this.require({ deps: [`Social`] });
							}

							const { Social } = this;
							const authorization = await Social.authorize({ provider: `facebook` });
							accessToken = authorization.accessToken;
						}

						// pass the token to the server
						const placeRequest = async (values = {}) => {
							try {
								const optionalKeys = [`email`, `firstName`, `lastName`];
								const body = { accessToken, locale };

								this.each(optionalKeys, (key) => {
									const value = values[key];

									if (this.isString(value)) {
										body[key] = value;
									}
								});

								const response = await XHR.ajax({
									body,
									REST: true,
									type: `POST`,
									url: `/sso/facebook`,
								});

								this.set({ skipCallbacks, value: response });

								// TODO: remove return if no one is using it
								return this.get({ force: false });
							} catch (reason) {
								const error = reason?.responseJSON;
								const isStandardError = error?.docType === `STANDARD_ERROR`;
								const isMissingFields = error?.type === `FACEBOOK_SIGN_IN_MISSING_FIELDS`;

								if (!(isStandardError && isMissingFields)) {
									throw reason;
								}

								return new Promise((resolve) => {
									const { details } = error;
									const { email = null, firstName = null, lastName = null } = details;

									App.loadOverlay({
										// Open the overlay
										type: `ConfirmFacebookDetails`,
										setup: {
											callbacks: {
												// continue when the overlay has closed
												submit(formValues) {
													// try submission again
													const result = placeRequest(formValues);
													resolve(result);
												},
											},
											email,
											firstName,
											lastName,
										},
									});
								});
							}
						};

						// allow pre-populating using User.logIn(options)
						await placeRequest({
							email: options.email,
							firstName: options.firstName,
							lastName: options.lastName,
						});
						break;
					}

					case `apple`:
					case `google`: {
						if (!this.isObject(this.Social)) {
							// avoid blocking unnecessarily
							await this.require({ deps: [`Social`] });
						}
						const { Social } = this;
						const response = await Social.authorize({ provider: method });
						// Apple's `/sso/apple` response wraps the profile as `{ userProfile, isPrivateEmail }`;
						// Google's `/sso/google` returns the profile directly.
						const user = method === `apple` ? response?.userProfile : response;

						this.set({ skipCallbacks, value: user });

						return this.get({ force: false });
					}

					default: {
						throw new Error(`User.logIn(): Unsupported login method: ${options.method}`);
					}
				}
			},
		},

		logOut: {
			async: true,
			public: true,
			defaults: {
				local: false,
			},
			async value(context, options) {
				const { App, Locale, XHR, data } = this;
				const { guestUser } = data;

				this.defaults(options, {
					owner: context,
				});

				Locale.setUserDefault({ key: null });

				App.clearFirstPartyStorage();
				this.assign(data, {
					guestUser: null,
					identified: false,
				});

				if (this.isNull(guestUser) && !options.local) {
					const type = `GET`;
					const url = `/logout`;
					const ignoreErrors = true;

					await XHR.ajax({ ignoreErrors, type, url });

					App.captureInsight({
						eventType: `page-view`,
						pageType: `LogOut`,
						path: `/logout`,
						title: `User logout`,
					});
				}

				this.set({ value: null });
			},
		},

		setBirthday: {
			async: true,
			public: true,
			async value(context, options) {
				const { Time, XHR } = this;
				const { value, eventId } = options;
				const [year, month, day] = value;
				const birthDate = new Date(year, month - 1, day);
				const birthday = Time.format(`%Y-%N-%D`, birthDate.getTime());

				const user = await this.get();
				const { id: userId } = user;
				const body = { birthday, eventId };
				const url = `/api/users/${userId}?action=br&version=2`;
				const response = await XHR.ajax({ body, REST: true, type: `PUT`, url });

				this.set({ value: response });
			},
		},

		editProfile: {
			async: true,
			public: true,
			async value(context, options) {
				const { App, XHR } = this;
				const user = await this.get();
				const { id: userId } = user;
				const url = `/api/users/${userId}?action=update&version=2`;
				const REST = true;
				const type = `PUT`;
				const {
					skipSuccessNotification = false,
					birthday,
					countryCodeAlpha2,
					emailOptin,
					firstName,
					backupEmail,
					gender,
					lastName,
					postalCode,
					phoneNumber,
				} = options;
				const body = {
					birthday,
					countryCodeAlpha2,
					emailOptin,
					firstName,
					backupEmail,
					gender,
					lastName,
					postalCode,
					phoneNumber,
				};
				const response = await XHR.ajax({ body, REST, type, url });

				if (!skipSuccessNotification) {
					App.notifyUser({
						type: `success`,
						message: `Account updated successfully`,
					});
				}

				this.set({ value: response });
			},
		},

		editEmail: {
			async: true,
			public: true,
			async value(context, options) {
				const { App, XHR } = this;
				const { data: body } = options;
				const user = await this.get();
				const { id: userId } = user;
				const url = `/api/users/${userId}?action=emailchange&version=2`;
				// ignoreUnrecoveredErrors: the caller (Overlays/EditUserEmail) toasts failures itself,
				// so the framework's low-priority error toast would double it; 403 re-login and
				// DataDome recovery still run.
				const response = await XHR.ajax({ body, REST: true, type: `PUT`, url, ignoreUnrecoveredErrors: true });

				App.notifyUser({
					type: `success`,
					message: `Your email was successfully updated`,
				});

				this.set({ value: response });
			},
		},

		editPassword: {
			async: true,
			public: true,
			async value(context, options) {
				const { App, XHR } = this;
				const { data: body } = options;
				const user = await this.get();
				const { id: userId } = user;
				const url = `/api/users/${userId}?action=passwordchange&version=2`;
				const response = await XHR.ajax({ body, REST: true, type: `PUT`, url });

				App.notifyUser({
					type: `success`,
					message: `Password updated successfully`,
				});

				this.set({ value: response });
			},
		},

		setPassword: {
			async: true,
			public: true,
			async value(context, options) {
				const { App, XHR } = this;
				const { data: body } = options;
				const url = `/setinitialpassword`;
				const response = await XHR.ajax({ body, REST: true, type: `POST`, url });

				App.notifyUser({
					type: `success`,
					message: `Password set successfully`,
				});

				this.set({ value: response });
			},
		},

		/**
		 * the call to make when a user is requesting a "forgot password" reset
		 */
		requestPasswordReset: {
			async: true,
			public: true,
			async value(context, options) {
				const { XHR } = this;
				const { data: body } = options;
				const url = `/forgot`;
				const type = `POST`;
				const REST = false;

				await XHR.ajax({ body, REST, type, url });
			},
		},

		resetPassword: {
			async: true,
			public: true,
			async value(context, options) {
				// the call to update the user password, after they're used via email
				const { App, XHR } = this;
				const { data: body } = options;
				const url = `/reset`;
				const type = `POST`;
				const REST = false;
				const ignoreErrors = true; // important for swallowing the automatic notifyUser() error in the response
				const response = await XHR.ajax({ body, ignoreErrors, REST, type, url });

				App.notifyUser({
					type: `success`,
					message: `Password updated successfully`,
				});

				return response;
			},
		},

		triggerCallbacks: {
			value(options) {
				const { override } = options;
				const { data } = this;
				const { user } = data;
				const value = this.isObject(override) ? override : user;

				if (this.isNull(value)) {
					this.subscriptions = this.filter(this.subscriptions, (subscription) => {
						if (this.isFunction(subscription.errorHandler)) {
							subscription.errorHandler.call(subscription.owner); // notify subscribers that the user was logged out
						}
						return subscription.subscribe; // discard the subscription if it was only temporary
					});
				} else {
					this.subscriptions = this.filter(this.subscriptions, (subscription) => {
						const userCopy = this.clone(value); // protect the internal value from external reference
						subscription.handler.call(subscription.owner, userCopy); // notify subscribers of a user update
						return subscription.subscribe; // discard the subscription if it was only temporary
					});
				}
			},
		},

		setDefaultMethod: {
			async: true,
			public: true,
			async value(context, options) {
				const { XHR } = this;
				const { token } = options;
				const url = `/api/defaultcards/${token}`;

				await XHR.ajax({ type: `POST`, url });

				// force a full user update
				this.get({ force: true });
			},
		},

		followEvent: {
			async: true,
			public: true,
			async value(context, options) {
				const { App, Events, XHR } = this;

				try {
					const { eventId } = options;
					const event = await Events.get({ id: eventId });
					const user = await this.get({
						fail: (reason) => {
							// user not logged in
							const { theme } = event;
							const followEventId = eventId; // the event id to submit, if it's a first-time registration
							const mode = `follow`;

							// pop open the login prompt
							return this.requestLogin({ followEventId, mode, theme });
						},
						force: false, // allow cached user object
					});
					const { followedEvents, id: userId } = user;
					const isFollowing = this.isArray(followedEvents) && followedEvents.includes(eventId);

					if (!isFollowing) {
						// not already following the event
						await XHR.ajax({
							type: `POST`,
							REST: true,
							url: `/api/users/${userId}/follows`,
							body: { eventId },
						});

						// notify the app of a notable event
						const eventType = `wishlist-add`;
						App.captureInsight({ eventId, eventType });
					}

					// do a manual user refresh
					await this.get({ force: true });
				} catch (reason) {
					// assume an authentication error
					const type = `error`;
					const message = `You must be logged in to follow events`;

					App.notifyUser({ message, type });
					// swallow the error
				}
			},
		},

		unfollowEvent: {
			async: true,
			public: true,
			async value(context, options) {
				const { App, Events, XHR } = this;

				try {
					const { eventId } = options;
					const event = await Events.get({ id: eventId });
					const user = await this.get({
						// fetch the user
						force: false, // allow cached user object
						fail(reason) {
							// user not logged in
							const { theme } = event;
							const mode = `follow`;

							// pop open the login prompt
							return this.requestLogin({ mode, theme });
						},
					});

					const { followedEvents } = user;
					const isFollowing = this.isArray(followedEvents) && followedEvents.includes(eventId);

					if (isFollowing) {
						// user is following the event, unfollow
						await XHR.ajax({
							type: `DELETE`,
							url: `/api/users/${user.id}/follows/${eventId}`,
						});

						// notify the app of a notable event
						const eventType = `wishlist-remove`;
						App.captureInsight({ eventId, eventType });
					} // else already removed, nothing to do

					// do a manual user refresh
					await this.get({ force: true });
				} catch (reason) {
					// assume an authentication error
					const type = `error`;
					const message = `You must be logged in to unfollow events`;

					App.notifyUser({ message, type });
					// swallow the error
				}
			},
		},

		getEventsForUser: {
			async: true,
			public: true,
			async value(context, options = {}) {
				const { App, XHR } = this;
				const { endDateAfter, endDateBefore, eventId, pageNumber, pageSize } = options;
				const params = {
					isExchange: true,
				};
				let url;

				// TODO - look into rewriting this to make params more flexible
				if (this.isInt(pageNumber)) {
					params.page = pageNumber;
				}

				if (this.isInt(pageSize)) {
					params.size = pageSize;
				}

				if (this.isInt(endDateAfter)) {
					params.endDateAfter = endDateAfter;
				}

				if (this.isInt(endDateBefore)) {
					params.endDateBefore = endDateBefore;
				}

				const user = await this.get();
				const { id: userId } = user;
				const serializedParams = App.serializeParams(params);

				if (eventId != null) {
					url = `/api/users/${userId}/events/${eventId}${
						this.isString(serializedParams, 1) ? `?${serializedParams}` : ``
					}`;
				} else {
					url = `/api/users/${userId}/events${this.isString(serializedParams, 1) ? `?${serializedParams}` : ``}`;
				}

				return await XHR.ajax({ type: `GET`, url });
			},
		},

		getRewardEvents: {
			async: true,
			public: true,
			async value(context, options = {}) {
				const { App, XHR } = this;
				const { params } = options; // page, size, endDateAfter, endDateBefore
				const serializedParams = App.serializeParams(params);
				const user = await this.get();
				const { id: userId } = user;
				const url = `/api/users/${userId}/reward-events?${serializedParams}`;

				return XHR.ajax({ type: `GET`, url });
			},
		},

		getFanOwnedItems: {
			async: true,
			public: true,
			async value(context, options = {}) {
				const { App, System, XHR } = this;
				const { isNativeApp } = System.get();
				// hasValidTickets - whether or not items must have valid tickets to be returned
				this.defaults(options, {
					hasValidTickets: false,
					pageNumber: 1,
					pageSize: 25,
				});
				const { eventId, hasValidTickets, pageNumber, pageSize, extraParams = {} } = options;
				const params = { ...extraParams, hasValidTickets, page: pageNumber, size: pageSize };
				const user = await this.get();
				const { id: userId } = user;
				let url;

				params.orderBy = `transactionDate`;
				params.orderByDirection = `DESC`;
				params.isExchange = true;

				const serializedParams = App.serializeParams(params);
				if (eventId == null) {
					url = `/api/users/${userId}/fan-owned-items?${serializedParams}`;
				} else {
					url = `/api/users/${userId}/events/${eventId}/fan-owned-items?${serializedParams}`;
				}

				try {
					const response = await XHR.ajax({ type: `GET`, url });
					if (isNativeApp) {
						// Fan-owned-items aren't sellable on native apps yet
						this.each(response, (item) => {
							item.isSellable = false;
						});
					}
					return response;
				} catch (error) {
					return `An error occurred while fetching your items. Please try again.`;
				}
			},
		},

		getUserSalesByOrderId: {
			async: true,
			public: true,
			async value(context, options = {}) {
				// TODO - add pagination back when BE is ready
				this.defaults(options, { hasValidTickets: false });
				const { App, XHR } = this;
				const { hasValidTickets, id /*pageNumber, pageSize*/ } = options;
				const params = { hasValidTickets }; //{ page: pageNumber, size: pageSize };
				const serializedParams = App.serializeParams(params);
				const user = await this.get();
				const { id: userId } = user;
				const url = `/api/users/${userId}/orders/${id}/sales?${serializedParams}`;

				return XHR.ajax({ type: `GET`, url });
			},
		},

		getUserSalesByTransferId: {
			async: true,
			public: true,
			async value(context, options = {}) {
				// TODO - add pagination back when BE is ready
				this.defaults(options, { hasValidTickets: false });

				const { App, XHR } = this;
				const { hasValidTickets, id /*pageNumber, pageSize*/ } = options;
				const params = { hasValidTickets }; //{ page: pageNumber, size: pageSize };
				const serializedParams = App.serializeParams(params);
				const user = await this.get();
				const { id: userId } = user;
				const url = `/api/users/${userId}/fan-transfers/${id}/sales?${serializedParams}`;

				return XHR.ajax({ type: `GET`, url });
			},
		},

		getGuestUser: {
			public: true,
			value(context, options) {
				return this.data.guestUser ?? null;
			},
		},

		amplifyEvent: {
			async: true,
			public: true,
			async value(context, options) {
				const { XHR } = this;
				const { eventId } = options;
				const user = await this.get();
				const { enrolledAmplifyEvents, id: userId } = user;

				if (!this.includes(enrolledAmplifyEvents, eventId)) {
					const url = `/api/users/${userId}/amplify-enrollments/${eventId}`;
					const response = await XHR.ajax({ type: `POST`, url });

					// force a full user update
					this.get({ force: true });
					return response;
				}
			},
		},

		getAmplifiedEvents: {
			async: true,
			public: true,
			defaults: {
				block: true,
			},
			async value(context, options) {
				const { Events, Utils, XHR } = this;
				const { block } = options;
				const user = await this.get();

				const { enrolledAmplifyEvents } = user;
				const numberOfEnrolledEvents = this.isArray(enrolledAmplifyEvents) ? enrolledAmplifyEvents.length : 0;
				const result = []; // start with an empty array

				if (this.isArray(enrolledAmplifyEvents, 1)) {
					const idsString = enrolledAmplifyEvents.join(`,`);
					const url = `/api/users/${user.id}/amplify-points?eventIds=${idsString}`;
					const type = `GET`;
					const by = `ids`;
					const eventsPromise = Events.get({ by, ids: enrolledAmplifyEvents });
					const pointMapsPromise = XHR.ajax({ block, type, url });
					const [events, pointMaps] = await Promise.all([eventsPromise, pointMapsPromise]);

					this.each(pointMaps, (map) => {
						// iterate over the point-maps
						const id = map.eventId;
						const event = this.find(events, { id }); // find the corresponding event

						if (this.isObject(event)) {
							// remove redundant keys
							delete map.eventId;
							delete map.userId;

							this.assign(event, map); // copy point properties to the event object
							result.push(event);
						}
					});
				}

				if (numberOfEnrolledEvents > result.length) {
					// sanity checking
					const resultIds = Utils.map(result, `id`);
					const missing = Utils.difference(enrolledAmplifyEvents, resultIds);
					const missingIds = missing.join(`,`);

					this.log({
						message: `User.getAmplifiedEvents(): Missing events ignored: ${missingIds}`,
						type: `warning`,
					});
				}

				return result;
			},
		},

		getRewardSnapshot: {
			async: true,
			public: true,
			async value(context, { id: eventId }) {
				if (!this.isInt(eventId)) {
					throw new Error(`User.getRewardSnapshot(): options.id is required`);
				}

				const { XHR } = this;
				const { id: userId } = await this.get();

				let response = await XHR.ajax({
					url: `/api/users/${userId}/amplify-reward-snapshots/${eventId}`,
					type: `GET`,
				});

				// initialize some helper properties
				response.totalPoints = response.points + response.pendingPoints;
				response.numSales = this.isArray(response.contributingOrders) ? response.contributingOrders.length : 0;

				return response;
			},
		},

		getCreditsForUser: {
			async: true,
			public: true,
			async value(context, { page, size }) {
				const { App, XHR } = this;
				const { id: userId } = await this.get();
				const params = {
					page,
					size,
				};

				// Remove undefined params
				this.each(Object.keys(params), (key) => {
					if (typeof params[key] === `undefined`) {
						delete params[key];
					}
				});

				return XHR.ajax({
					type: `GET`,
					url: `/api/users/${userId}/credits?${App.serializeParams(params)}`,
					ignoreErrors: true,
				});
			},
		},

		getCreditsForGroup: {
			async: true,
			public: true,
			async value(context, { groupId }) {
				const { XHR } = this;
				const { id: userId } = await this.get();

				return XHR.ajax({
					type: `GET`,
					url: `/api/users/${userId}/groups/${groupId}/credits`,
					ignoreErrors: true,
				});
			},
		},

		getRefundSummary: {
			async: true,
			public: true,
			async value(context, { orderId, selectedItems }) {
				const { XHR } = this;
				const { id: userId } = await this.get();
				const body = { selectedItems };

				return XHR.ajax({
					body,
					REST: true,
					type: `POST`,
					url: `/api/users/${userId}/orders/${orderId}/refund-preview`,
				});
			},
		},

		submitRefund: {
			async: true,
			public: true,
			async value(context, { orderId, selectedItems }) {
				const { XHR } = this;
				const { id: userId } = await this.get();
				const body = { selectedItems };

				return XHR.ajax({
					body,
					REST: true,
					type: `POST`,
					url: `/api/users/${userId}/orders/${orderId}/refund`,
				});
			},
		},

		linkAccount: {
			async: true,
			public: true,
			defaults: {
				provider: `facebook`,
			},
			async value(context, options) {
				const { XHR } = this;
				const { provider } = options;

				switch (provider) {
					case `facebook`: {
						// Get FB auth token
						if (!this.isObject(this.Social)) {
							// avoid blocking unnecessarily
							await this.require({ deps: [`Social`] });
						}

						const { Social } = this;
						const authorization = await Social.authorize({ provider: `facebook` });
						const { accessToken } = authorization;
						const url = `/fb/link?version=2`;
						const body = { accessToken };
						const type = `POST`;

						return XHR.ajax({ body, type, url });
					}
				}
			},
		},

		unlinkAccount: {
			async: true,
			public: true,
			defaults: {
				provider: `facebook`,
			},
			value(context, options) {
				const { XHR } = this;
				const { provider } = options;

				const url = `/sso/unlink`;
				const data = { provider: provider.toUpperCase() }; // FACEBOOK, APPLE, GOOGLE
				const type = `GET`;

				return XHR.ajax({ type, url, data });
			},
		},

		validatePassword: {
			public: true,
			value(context, value) {
				const tests = {
					minMaxLength: {
						message: `Between 12 and 50 characters`,
						result: this.isString(value, 12) && value.length <= 50,
					},
					upperCase: {
						message: `At least one uppercase character`,
						result: this.isString(value) && /[A-Z]/.test(value),
					},
					lowerCase: {
						message: `At least one lowercase character`,
						result: this.isString(value) && /[a-z]/.test(value),
					},
					number: {
						message: `At least one number`,
						result: this.isString(value) && /\d/.test(value),
					},
					consecutive: {
						message: `No character repeated more than twice in a row`,
						result: this.isString(value) && !/(.)\1{2,}/.test(value),
					},
				};
				let tooltipMessage = `The password must have:`;
				let validates = true;

				this.each(tests, (test) => {
					let style = `text-align: left;`;
					let icon;

					if (test.result) {
						style += `color: green;`;
						icon = `&#10004;`;
					} else {
						style += `color: red;`;
						icon = `&#10008;`;
						validates = false;
					}
					tooltipMessage += `<div style="${style}">${icon} ${test.message}</div>`;
				});

				return { validates, tooltipMessage };
			},
		},

		getAvailablePaymentMethods: {
			public: true,
			async: true,
			async value(context, options) {
				const { paymentProcessorType: eventPaymentProcessorType } = options;
				let user = await this.get();
				let defaultToken;

				if (this.isString(user, 1)) {
					user = JSON.parse(user);
				}

				// Calculate the available payment methods
				const paymentMethods = this.filter(user.paymentMethods, (method, i) => {
					const { paymentProcessorType, token } = method;

					if (paymentProcessorType !== eventPaymentProcessorType) {
						return false; // BRAINTREE | STRIPE mis-match
					}

					if (method.default) {
						// default method found, and is being included
						defaultToken = token; // save a reference to the default method
					}

					return true;
				});

				if (!this.isString(defaultToken) && this.isArray(paymentMethods, 1)) {
					paymentMethods[0].default = true; // no default method present, use the first one
				}

				return paymentMethods;
			},
		},

		getAvailablePaymentMethodsForAgentUse: {
			public: true,
			async: true,
			async value(context, options) {
				const { XHR, data, promises } = this;
				const { paymentProcessorType: eventPaymentProcessorType } = options;
				if (!(data.paymentMethodsRequestPending || promises.paymentMethods)) {
					promises.paymentMethods = XHR.ajax({
						url: `/api/user/${options.userId}/available-payment-methods?groupId=${options.groupId}`,
						then(response) {
							data.paymentMethodsRequestPending = false;

							// sort the payment methods by default
							response.sort((a, b) => {
								return b.default - a.default;
							});

							return response;
						},
						fail(reason) {
							data.paymentMethodsRequestPending = false;
							this.set({
								value: null,
							});
							throw reason;
						},
					});

					data.paymentMethodsRequestPending = true;
				}

				const allPaymentMethods = await promises.paymentMethods;
				let defaultToken;

				// calculate the available payment methods
				const paymentMethods = this.filter(allPaymentMethods, (method) => {
					const { paymentProcessorType, token } = method;

					if (paymentProcessorType !== eventPaymentProcessorType) {
						return false; // BRAINTREE | STRIPE mis-match
					}

					if (method.default) {
						// default method found, and is being included
						defaultToken = token;
					}

					return true;
				});

				if (!this.isString(defaultToken) && this.isArray(paymentMethods, 1)) {
					paymentMethods[0].default = true; // no default method present, use the first one
				}

				return paymentMethods;
			},
		},

		unsubscribe: {
			public: true,
			defaults: {},
			value(context, options) {
				this.defaults(options, {
					owner: context,
				});

				this.extract(this.subscriptions, options);
			},
		},

		onDie: {
			public: true,
			value(context, options) {
				const owner = context; // remove all subscriptions from the requester
				this.extract(this.subscriptions, { owner });
			},
		},

		deleteAccount: {
			async: true,
			public: true,
			value(context, options) {
				const { App, XHR } = this;

				return this.get({
					then: (user) =>
						XHR.ajax({
							url: `/api/users/${user.id}/account-delete-request`,
							type: `GET`,
							REST: true,
							then: (response) => {
								App.notifyUser({
									type: `success`,
									message: response.message,
									duration: 10000,
								});
							},
							fail: (reason) => {
								const { responseJSON } = reason;

								if (this.isObject(responseJSON)) {
									App.notifyUser({
										type: `warning`,
										message: responseJSON.message,
										duration: 10000,
									});
								}
							},
						}),
				});
			},
		},

		getResaleAccountStatus: {
			async: true,
			public: true,
			value() {
				const { XHR } = this;

				return XHR.ajax({
					url: `/api/resale-custom-account`,
					type: `GET`,
					REST: true,
				});
			},
		},

		addBackupEmail: {
			public: true,
			async: true,
			async value(context, options) {
				const { XHR } = this;
				const { backupEmail, verificationToken } = options;
				// SEV-002: a verified write, with the proof from a kind:BACKUP code sent to backupEmail.
				const body = {
					backupEmail,
					verificationToken,
				};
				const url = `/api/user/backup-email?action=UPDATE`;
				const type = `POST`;
				const REST = true;

				// EditUserEmail reports a failure itself; skip only the framework's low-priority toast so the
				// 403 re-login and DataDome recovery still run (as editEmail does).
				await XHR.ajax({ body, REST, type, url, ignoreUnrecoveredErrors: true });

				// The response is a UserProfile, not the session user; refetch so later reads (e.g. the next
				// EditUserEmail's same-address check) see the new backup.
				await this.get({ force: true });
			},
		},

		setBackupEmailExited: {
			async: true,
			async value() {
				const { XHR } = this;
				const body = {};
				const url = `/api/user/backup-email?action=EXIT`;
				const type = `POST`;
				const REST = true;

				await XHR.ajax({ body, REST, type, url });
			},
		},

		// MARK:- Confirm Email (CE2)

		sendConfirmEmailOtp: {
			public: true,
			async: true,
			async value(context, options) {
				const { XHR } = this;
				const { email } = options;
				const body = { email };
				const url = `/api/user/confirm-email/send`;
				const type = `POST`;
				const REST = true;

				// CEF-020: LogIn's send handler toasts every rejection itself, so skip only the framework's
				// low-priority toast (it would repeat the raw server message). High-priority recovery -- the
				// 403 re-login and DataDome challenge -- still runs, unlike ignoreErrors on resend below.
				return await XHR.ajax({ body, REST, type, url, ignoreUnrecoveredErrors: true });
			},
		},

		resendConfirmEmailOtp: {
			public: true,
			async: true,
			async value(context, options) {
				const { XHR } = this;
				const { token } = options;
				const body = {};
				const url = `/api/user/confirm-email/resend?token=${encodeURIComponent(token)}`;
				const type = `POST`;
				const REST = true;

				// LogIn's resend handler is the sole caller and classifies every rejection itself
				// (throttle, dead token, other) before deciding what the fan sees. Without this, XHR
				// threads the framework's own error handlers into the promise chain *inside* ajax --
				// they fire whether or not the caller attaches a .catch() -- so a dead token that this
				// ticket recovers from showed the fan "This verification session expired" immediately
				// before "Verification code sent."
				return await XHR.ajax({ body, REST, type, url, ignoreErrors: true });
			},
		},

		verifyConfirmEmail: {
			public: true,
			async: true,
			async value(context, options) {
				const { XHR } = this;
				const { code, token } = options;
				const body = { code, token };
				const url = `/api/user/confirm-email/verify`;
				const type = `POST`;
				const REST = true;

				// CEF-020: see sendConfirmEmailOtp. Without this, EMAIL_USED showed the server's "This email
				// address is already in use." alongside LogIn's own copy.
				return await XHR.ajax({ body, REST, type, url, ignoreUnrecoveredErrors: true });
			},
		},

		keepCurrentConfirmEmail: {
			public: true,
			async: true,
			async value() {
				const { XHR } = this;
				const body = {};
				const url = `/api/user/confirm-email/keep-current`;
				const type = `POST`;
				const REST = true;

				return await XHR.ajax({ body, REST, type, url });
			},
		},

		dismissConfirmEmail: {
			public: true,
			async: true,
			async value() {
				const { XHR } = this;
				const body = {};
				const url = `/api/user/confirm-email/dismiss`;
				const type = `POST`;
				const REST = true;

				return await XHR.ajax({ body, REST, type, url });
			},
		},

		markConfirmEmailShown: {
			public: true,
			async: true,
			async value() {
				const { XHR } = this;
				const body = {};
				const url = `/api/user/confirm-email/shown`;
				const type = `POST`;
				const REST = true;

				return await XHR.ajax({ body, REST, type, url });
			},
		},

		// MARK:- Signup Verification (TXP-8118)

		sendSignupOtp: {
			public: true,
			async: true,
			async value(context, options) {
				const { Locale, XHR } = this;
				const { body, follow } = options;
				const localeInfo = Locale.get();
				const { selected: selectedLocale } = localeInfo;
				const { key: locale } = selectedLocale;

				body.locale = locale;

				let url = `/api/signup/send`;
				if (this.isInt(follow, 1)) {
					url += `?follow=${follow}`;
				}
				const type = `POST`;
				const REST = true;

				return await XHR.ajax({ body, REST, type, url });
			},
		},

		verifySignupOtp: {
			public: true,
			async: true,
			async value(context, options) {
				const { XHR } = this;
				const { code, password, token } = options;
				const body = { code, password, token };
				const url = `/api/signup/verify`;
				const type = `POST`;
				const REST = true;

				return await XHR.ajax({ body, REST, type, url });
			},
		},

		getOtpErrorMessage: {
			public: true,
			value(context, reason, fallback = `Something went wrong. Please try again.`) {
				const error = reason?.responseJSON || reason;
				const type = error?.type;

				// CEF-020: the server's 1141 text ("This email address is already in use.") is not the
				// product copy, so EMAIL_USED never defers to error.message.
				if (type === `EMAIL_USED`) {
					return `This email can’t be added. Try another address.`;
				}

				// Prefer type over numeric code; suppress null messages (OTP_REQUIRED convention).
				if (this.isString(error?.message, 1)) {
					return error.message;
				}

				switch (type) {
					case `OTP_CHALLENGE_INVALID_PIN`:
						return `Invalid verification code. Please try again.`;
					case `OTP_CHALLENGE_EXPIRED_PIN`:
					case `OTP_CHALLENGE_NOT_FOUND`:
					case `OTP_CHALLENGE_SUPERSEDED`:
					case `OTP_CHALLENGE_SEND_FAILED`:
					case `MFA_TOKEN_NOT_FOUND`:
						return `This verification session expired. Please start again.`;
					case `OTP_LOGIN_THROTTLED`:
					case `MFA_MAX_SEND_ATTEMPTS`:
						return `Too many attempts. Please wait and try again.`;
					case `SIGN_UP_ERROR`:
						return `An account already exists for this email address. Please log in instead.`;
					default:
						return fallback;
				}
			},
		},

		/**
		 * CEF-017: shared OTP error classification, previously duplicated verbatim in
		 * Overlays/CodeVerification.js and Modules/LogIn.js. Pure string-matching on `error.type`
		 * against the server's rejection vocabulary (StandardErrorEnum) -- callers keep the
		 * fallback-call and error-display logic that surrounds this check, since that part is
		 * genuinely transport-specific (toast vs. field error, MFA.sendCode vs. User.sendConfirmEmailOtp).
		 * @param {Object} reason - rejection from a resend call
		 * @returns {Boolean} true if the server reports the resend itself is rate-limited
		 */
		isThrottledOtpError: {
			public: true,
			value(context, reason) {
				const type = (reason?.responseJSON || reason)?.type;

				return type === `MFA_RESEND_COOLDOWN` || type === `MFA_MAX_SEND_ATTEMPTS`;
			},
		},

		/**
		 * CEF-017: see isThrottledOtpError above. Only MFA_TOKEN_NOT_FOUND (1112) and
		 * OTP_CHALLENGE_EXPIRED_PIN (1140) mean the challenge itself is gone -- do not add
		 * speculative members, a type the server never sends is a branch no one can reach or test.
		 * @param {Object} reason - rejection from a resend call
		 * @returns {Boolean} true if the current token/challenge can never be resent successfully
		 */
		isDeadOtpToken: {
			public: true,
			value(context, reason) {
				const type = (reason?.responseJSON || reason)?.type;

				return type === `MFA_TOKEN_NOT_FOUND` || type === `OTP_CHALLENGE_EXPIRED_PIN`;
			},
		},

		/**
		 * CEF-036: the one place that knows how a login rejection signals an MFA challenge (401
		 * MFA_REQUIRED carrying a preAuthToken). logIn uses it to skip recording a failed login;
		 * Modules/LogIn.js uses it to open the code modal.
		 * @param {Object} reason - rejection from logIn
		 * @returns {String|undefined} the pre-auth token when the password was right but MFA is required
		 */
		getMfaPreAuthToken: {
			public: true,
			value(context, reason) {
				return reason?.responseJSON?.preAuthToken;
			},
		},

		/**
		 * @function getBenefitsGroupsList
		 * @param {Object} context
		 * @param {number} userId
		 * @param {Object} options
		 * @param {number} options.page
		 * @param {number} options.size
		 * @returns {Promise<Array>}
		 * @description Get a list of benefits groups for a given user
		 *
		 * @example <caption>const groups = await User.getBenefitsGroupsList({ page: 1, size: 25 });</caption>
		 */
		getBenefitsGroupsList: {
			async: true,
			public: true,
			async value(context, { page, size }) {
				const { App, XHR } = this;
				const { id: userId } = await this.get();
				const params = {
					page,
					size,
				};

				// Remove undefined params
				this.each(Object.keys(params), (key) => {
					if (typeof params[key] === `undefined`) {
						delete params[key];
					}
				});

				return await XHR.ajax({
					type: `GET`,
					url: `/api/benefits/${userId}/groups?${App.serializeParams(params)}`,
				});
			},
		},

		getBenefitsByGroupId: {
			async: true,
			public: true,
			async value(context, { groupId, page, size }) {
				const { App, XHR } = this;
				const { id: userId } = await this.get();
				const params = {
					page,
					size,
				};

				// Remove undefined params
				this.each(Object.keys(params), (key) => {
					if (typeof params[key] === `undefined`) {
						delete params[key];
					}
				});

				return await XHR.ajax({
					type: `GET`,
					url: `/api/benefits/${userId}/${groupId}?${App.serializeParams(params)}`,
				});
			},
		},
	},
});

/**
 * @typedef {Object} UserBillingAddress
 * @property {String} city
 * @property {String} countryCodeAlpha2
 * @property {String} docType
 * @property {String} docVer
 * @property {String} extendedAddress
 * @property {String} firstname
 * @property {String} id
 * @property {String} lastname
 * @property {String} postalCode
 * @property {String} state
 * @property {String} streetAddress
 */

/**
 * @typedef {Object} UserFacebookInfo
 * @property {String} accessToken
 * @property {String} facebookId
 */

/**
 * @typedef {Object} UserPaymentMethod
 * @property {String} billingId
 * @property {String} cardType
 * @property {Boolean} default
 * @property {String} last4
 * @property {String} paymentProcessorType
 * @property {String} token
 * @property {String} type
 */

/**
 * @typedef {Object} UserPrivacyChoices
 * @property {Boolean} doNotSharePII
 * @property {Boolean} limitUseOfPII
 * @property {String} type
 */

/**
 * @typedef {Object} User
 * @property {String} amplifierId
 * @property {String} amplifierUrl
 * @property {Object<UserBillingAddress>} billingAddresses - map of adddesses
 * @property {Array} birthday - [year, month, day] format
 * @property {?String} countryCodeAlpha2
 * @property {?String} defaultLocale
 * @property {String} email
 * @property {String} emailHash
 * @property {Boolean} emailOptin
 * @property {Boolean} emailSet
 * @property {UserFacebookInfo} facebookInfo
 * @property {Boolean} fbLinked
 * @property {String} firstName
 * @property {?String} gender
 * @property {Object} groupOptIn
 * @property {Number} id
 * @property {String} lastName
 * @property {Boolean} newFBUser
 * @property {UserPaymentMethod[]} paymentMethods
 * @property {?String} phoneNumber
 * @property {?String} postalCode
 * @property {?UserPrivacyChoices[]} privacyChoices
 * @property {Object} purchasedHoldQuantityMap - A mapping from hold id to the quantity the fan has purchased
 * @property {Object} purchasedSaleQuantityMap - A mapping from sale id to the quantity the fan has purchased
 * @property {String} state CONFIRMED | FB_UNCONFIRMED | GUEST | UNCONFIRMED
 * @property {Boolean} temp
 */

// TODO: verify where the "guest user" object comes from; does its schema need to update?

//# sourceMappingURL=User.js.map